// redux/slices/installmentsSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";
import { updateAppointment, deleteAppointment } from "./appointmentsSlice";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
    error?.response?.data ||
    error?.message ||
    fallback;

// Сервер отдаёт DECIMAL строкой — приводим к числу один раз при записи в стор.
const normalizeRow = (row, appointment) => ({
    ...row,
    amount: Number(row?.amount) || 0,
    appointment: row?.appointment ?? appointment ?? null,
});

const pickAppointment = (a) =>
    a
        ? {
            id: a.id,
            uid: a.uid,
            firstNameContact: a.firstNameContact,
            lastNameContact: a.lastNameContact,
            phoneContact: a.phoneContact,
            dateOfAppointment: a.dateOfAppointment,
            selectedHour: a.selectedHour,
            statusClient: a.statusClient,
            isNewUser: a.isNewUser,
        }
        : null;

const removeByAppointment = (state, appointmentId) => {
    Object.keys(state.byId).forEach((id) => {
        if (String(state.byId[id]?.appointmentId) === String(appointmentId)) {
            delete state.byId[id];
        }
    });
};

/* ========================= PRIVATE THUNKS ========================= */

// Платежи рассрочки за период для набора мастеров салона.
// from/to — YYYY-MM-DD, фильтр по дате платежа (dueDate), а не по дате встречи.
export const fetchInstallmentsByRange = createAsyncThunk(
    "installments/fetchByRange",
    async ({ uids, from, to }, { rejectWithValue }) => {
        try {
            const uidList = (uids || []).map(String).filter(Boolean);
            if (!uidList.length) throw new Error("UID не передан");

            const query = `uids=${encodeURIComponent(uidList.join(","))}&from=${from}&to=${to}`;
            const response = await apiRequest("GET", `/appointment-installments?${query}`);
            const rows = response?.data || response;

            return {
                rows: Array.isArray(rows) ? rows : [],
                uids: uidList,
                from,
                to,
            };
        } catch (error) {
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
                try {
                    await clearJwtToken();
                } catch { }
            }

            return rejectWithValue(getError(error, "fetchInstallmentsByRange failed"));
        }
    }
);

// Весь график рассрочки одной встречи.
export const fetchInstallmentsByAppointment = createAsyncThunk(
    "installments/fetchByAppointment",
    async (appointmentId, { rejectWithValue }) => {
        try {
            if (!appointmentId) throw new Error("ID встречи не передан");

            const response = await apiRequest(
                "GET",
                `/appointment-installments/appointment/${appointmentId}`
            );
            const rows = response?.data || response;

            return {
                appointmentId,
                rows: Array.isArray(rows) ? rows : [],
            };
        } catch (error) {
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
                try {
                    await clearJwtToken();
                } catch { }
            }

            return rejectWithValue(getError(error, "fetchInstallmentsByAppointment failed"));
        }
    }
);

/* ========================= SELECTORS ========================= */

const selectById = (state) => state.installments?.byId || {};

const sortRows = (a, b) =>
    String(a.dueDate).localeCompare(String(b.dueDate)) ||
    Number(a.appointmentId) - Number(b.appointmentId) ||
    Number(a.cardIndex) - Number(b.cardIndex) ||
    Number(a.installmentNumber) - Number(b.installmentNumber);

// selectInstallmentsInRange(state, { uids, from, to }) — params оборачивать в useMemo
export const selectInstallmentsInRange = createSelector(
    [selectById, (state, params) => params],
    (byId, { uids = [], from, to } = {}) => {
        const uidSet = new Set(uids.map(String));

        return Object.values(byId)
            .filter(
                (row) =>
                    uidSet.has(String(row.uid)) &&
                    (!from || row.dueDate >= from) &&
                    (!to || row.dueDate <= to)
            )
            .sort(sortRows);
    }
);

export const selectInstallmentsForAppointment = createSelector(
    [selectById, (state, appointmentId) => appointmentId],
    (byId, appointmentId) =>
        Object.values(byId)
            .filter((row) => String(row.appointmentId) === String(appointmentId))
            .sort(sortRows)
);

/* ========================= STATE ========================= */

const initialState = {
    byId: {},
    status: "idle",
    error: null,
};

/* ========================= SLICE ========================= */

const installmentsSlice = createSlice({
    name: "installments",
    initialState,
    reducers: {
        clearInstallments: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchInstallmentsByRange.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchInstallmentsByRange.fulfilled, (state, action) => {
                state.status = "succeeded";

                const { rows, uids, from, to } = action.payload || {};
                const uidSet = new Set(uids || []);

                // Ответ — полный срез периода: убираем строки, которых больше нет на сервере.
                Object.keys(state.byId).forEach((id) => {
                    const row = state.byId[id];
                    if (
                        uidSet.has(String(row.uid)) &&
                        row.dueDate >= from &&
                        row.dueDate <= to
                    ) {
                        delete state.byId[id];
                    }
                });

                (rows || []).forEach((row) => {
                    state.byId[row.id] = normalizeRow(row);
                });
            })
            .addCase(fetchInstallmentsByRange.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "fetchInstallmentsByRange failed";
            })

            .addCase(fetchInstallmentsByAppointment.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchInstallmentsByAppointment.fulfilled, (state, action) => {
                state.status = "succeeded";

                const { appointmentId, rows } = action.payload || {};
                removeByAppointment(state, appointmentId);
                (rows || []).forEach((row) => {
                    state.byId[row.id] = normalizeRow(row);
                });
            })
            .addCase(fetchInstallmentsByAppointment.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "fetchInstallmentsByAppointment failed";
            })

            // PUT встречи возвращает актуальный график, если менялись paymentSplit/статус.
            .addCase(updateAppointment.fulfilled, (state, action) => {
                const installments = action.payload?.installments;
                if (!Array.isArray(installments)) return;

                const appointment = action.payload?.appointment;
                const appointmentId = appointment?.id ?? action.meta?.arg?.id;
                if (appointmentId == null) return;

                removeByAppointment(state, appointmentId);

                const appointmentInfo = pickAppointment(appointment);
                installments.forEach((row) => {
                    state.byId[row.id] = normalizeRow(row, appointmentInfo);
                });
            })

            .addCase(deleteAppointment.fulfilled, (state, action) => {
                removeByAppointment(state, action.payload);
            })

            .addCase(resetStore, () => initialState);
    },
});

/* ========================= ACTIONS ========================= */

export const { clearInstallments } = installmentsSlice.actions;

export default installmentsSlice.reducer;